import * as React from "react";
import { useAuth } from "./auth";
import { AUTH_KEY, loadAuth, type StoredAuth } from "@shared/lib/tokenStorage";
import {
  refresh as refreshAuth,
  type RefreshResponse,
} from "@shared/lib/api/auth";
import { Trans } from "react-i18next";
import { useOnline } from "@shared/lib/pwa";

type RestoredSession = {
  accessToken: string;
  refreshToken: string | null;
  user: StoredAuth["user"];
  expiresAt: number | null;
  tenantId: string | null;
  remember: boolean;
};

function detectRemember(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return !!window.localStorage.getItem(AUTH_KEY);
  } catch {
    return false;
  }
}

export type SessionHydrationDecision = "noop" | "refresh" | "login" | "logout";

function hasUsableAccessToken(stored: StoredAuth | null, now = Date.now()) {
  return Boolean(
    stored?.accessToken && (!stored.expiresAt || now < stored.expiresAt),
  );
}

function shouldRefreshUsableAccessToken(stored: StoredAuth, now = Date.now()) {
  return (
    typeof stored.expiresAt === "number" && stored.expiresAt - now < 30_000
  );
}

function isCookieBackedRefreshToken(stored: StoredAuth | null) {
  return (
    typeof stored?.refreshToken === "string" &&
    stored.refreshToken.toLowerCase() === "cookie"
  );
}

function getRefreshExpiry(response: RefreshResponse): number | null {
  if (typeof response.expiresAt === "number") return response.expiresAt;
  return response.expiresIn ? Date.now() + response.expiresIn * 1000 : null;
}

function buildStoredSession(
  stored: StoredAuth,
  remember: boolean,
  tenantId: string | null,
): RestoredSession {
  return {
    accessToken: stored.accessToken as string,
    refreshToken: stored.refreshToken,
    user: stored.user ?? null,
    expiresAt: stored.expiresAt ?? null,
    tenantId,
    remember,
  };
}

function buildRefreshedSession(
  stored: StoredAuth,
  response: RefreshResponse,
  remember: boolean,
  tenantId: string | null,
): RestoredSession {
  return {
    accessToken: response.accessToken,
    refreshToken:
      typeof response.refreshToken !== "undefined"
        ? response.refreshToken
        : stored.refreshToken,
    user: response.user ?? stored.user ?? null,
    expiresAt: getRefreshExpiry(response),
    tenantId,
    remember,
  };
}

export function shouldUseStoredAccessBeforeRefresh(
  stored: StoredAuth,
  remember: boolean,
  now = Date.now(),
) {
  if (!hasUsableAccessToken(stored, now)) return false;
  return remember || !isCookieBackedRefreshToken(stored);
}

export function resolveSessionHydrationDecision(
  stored: StoredAuth | null,
  now = Date.now(),
): SessionHydrationDecision {
  if (!stored?.accessToken && !stored?.refreshToken) {
    return "noop";
  }

  // Prefer refresh-token based restore when possible so stored access tokens are
  // not treated as authoritative longer than the server session allows.
  if (stored?.refreshToken) {
    return "refresh";
  }

  // Use existing accessToken if it's still alive (or we don't know the expiry yet)
  if (hasUsableAccessToken(stored, now)) {
    return "login";
  }

  return "logout";
}

export function SessionHydrator({ children }: { children: React.ReactNode }) {
  const online = useOnline();
  const { login, logout } = useAuth();
  const sessionActions = React.useRef({ login, logout });
  sessionActions.current = { login, logout };
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const { login, logout } = sessionActions.current;

    const hydrate = async () => {
      try {
        // AuthProvider already restored the saved identity. Offline pages are gated
        // separately; a network failure must not destroy the POS session or queue.
        if (!online) return;
        const stored = loadAuth();
        const now = Date.now();
        const tenantId =
          typeof stored?.tenantId !== "undefined" ? stored.tenantId : null;
        const remember =
          typeof stored?.remember === "boolean"
            ? stored.remember
            : detectRemember();
        const decision = resolveSessionHydrationDecision(stored, now);

        if (decision === "noop") {
          return;
        }

        if (decision === "refresh" && stored?.refreshToken) {
          if (shouldUseStoredAccessBeforeRefresh(stored, remember, now)) {
            login(buildStoredSession(stored, remember, tenantId));

            if (shouldRefreshUsableAccessToken(stored, now)) {
              void refreshAuth(stored.refreshToken)
                .then((refreshed) => {
                  if (cancelled) return;

                  login(
                    buildRefreshedSession(
                      stored,
                      refreshed,
                      remember,
                      tenantId,
                    ),
                  );
                })
                .catch(() => {
                  // Keep the usable access token; API requests will re-check auth and force logout if needed.
                });
            }
            return;
          }

          try {
            const refreshed = await refreshAuth(stored.refreshToken);
            if (cancelled) return;

            login(buildRefreshedSession(stored, refreshed, remember, tenantId));
            return;
          } catch {
            // fallthrough to hard logout below for expired/non-recoverable access tokens
          }
        }

        if (decision === "login" && stored?.accessToken) {
          login(buildStoredSession(stored, remember, tenantId));
          return;
        }

        if (!cancelled) {
          await Promise.resolve(
            logout({ reason: "session-expired", server: false }),
          );
        }
      } catch {
        if (!cancelled) {
          await Promise.resolve(
            logout({ reason: "session-expired", server: false }),
          );
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
    // This is startup/reconnect recovery. A normal login changing its callbacks
    // must not start another cookie rotation while the login route redirects.
  }, [online]);

  if (!ready) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center text-sm text-muted-foreground">
        <Trans i18nKey="ui:loadingTitleCaseEllipsisCompact">{"Loading..."}</Trans>
      </div>
    );
  }

  return <>{children}</>;
}
